/**
 * The removal's deadline is the unenrollment XDR's timebound (`expires_at`,
 * four days after initiation per docs/sdp/multisig.md). The design shows it
 * two ways:
 *
 *  - long:  "Sunday, Oct 4, 2026 at 2:15 PM EDT"  (review screens)
 *  - short: "Oct 4, 2026 at 2:15 PM"              (Settings banners, sign modal)
 *
 * Both are rendered in the viewer's own timezone, matching the assisted
 * recovery deadline in `RecoveryQRModal`. `Intl` joins date and time with a
 * comma when given field options, so the two halves are formatted apart and
 * joined with " at " to match the design.
 */
export type UnenrollmentDeadlineStyle = "long" | "short";

type FormatOptions = {
  style?: UnenrollmentDeadlineStyle;
  /** Override for tests; defaults to the browser's timezone. */
  timeZone?: string;
};

export const formatUnenrollmentDeadline = (
  iso: string | null | undefined,
  { style = "long", timeZone }: FormatOptions = {},
): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const datePart = new Intl.DateTimeFormat("en-US", {
    ...(style === "long" ? { weekday: "long" } : {}),
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone,
  }).format(date);

  const timePart = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    ...(style === "long" ? { timeZoneName: "short" } : {}),
    timeZone,
  }).format(date);

  return `${datePart} at ${timePart}`;
};
